import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { RotateCcw, Trash2, Package, Star, Grid2X2 } from "lucide-react";

/**
 * AdminTrash — Recently Deleted tab.
 * Shows deleted products, catalogues, orders, reviews, and custom tiles.
 * Restore + permanent delete for each. Auto-purge after 30 days.
 */
export default function AdminTrash() {
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [deletedCatalogues, setDeletedCatalogues] = useState([]);
  const [deletedOrders, setDeletedOrders] = useState([]);
  const [deletedReviews, setDeletedReviews] = useState([]);
  const [deletedTiles, setDeletedTiles] = useState([]);

  const reloadDeletedProducts = useCallback(
    () => adminApi().get("/admin/products/deleted").then((r) => setDeletedProducts(r.data || [])).catch(() => {}),
    [],
  );
  const reloadDeletedCatalogues = useCallback(
    () => adminApi().get("/admin/catalogues/deleted").then((r) => setDeletedCatalogues(r.data || [])).catch(() => {}),
    [],
  );
  const reloadDeletedOrders = useCallback(
    () => adminApi().get("/admin/orders/deleted").then((r) => setDeletedOrders(r.data || [])).catch(() => {}),
    [],
  );
  const reloadDeletedReviews = useCallback(
    () => adminApi().get("/admin/reviews/deleted").then((r) => setDeletedReviews(r.data || [])).catch(() => {}),
    [],
  );
  const reloadDeletedTiles = useCallback(
    () => adminApi().get("/admin/custom-tiles/deleted").then((r) => setDeletedTiles(r.data || [])).catch(() => {}),
    [],
  );

  useEffect(() => {
    reloadDeletedProducts(); reloadDeletedCatalogues();
    reloadDeletedOrders(); reloadDeletedReviews(); reloadDeletedTiles();
  }, [reloadDeletedProducts, reloadDeletedCatalogues, reloadDeletedOrders, reloadDeletedReviews, reloadDeletedTiles]);

  const restoreProduct = async (p) => {
    try {
      await adminApi().post(`/admin/products/${p.id}/restore`);
      toast.success(`Restored "${p.name}"`);
      reloadDeletedProducts();
    } catch (err) { toast.error(err?.response?.data?.detail || "Restore failed"); }
  };
  const permanentlyDeleteProduct = async (p) => {
    if (!window.confirm(`Permanently delete "${p.name}"?\n\nThis cannot be undone. All images, variants and reviews for this product will also be removed.`)) return;
    try {
      await adminApi().delete(`/admin/products/${p.id}/permanent`);
      toast.success(`"${p.name}" permanently deleted`);
      reloadDeletedProducts();
    } catch (err) { toast.error(err?.response?.data?.detail || "Permanent delete failed"); }
  };
  const restoreCatalogue = async (c) => {
    try {
      await adminApi().post(`/admin/catalogues/${c.slug}/restore`);
      toast.success(`Restored "${c.name}"`);
      reloadDeletedCatalogues();
    } catch (err) { toast.error(err?.response?.data?.detail || "Restore failed"); }
  };
  const permanentlyDeleteCatalogue = async (c) => {
    if (!window.confirm(`Permanently delete "${c.name}"?\n\nThis cannot be undone. All images, videos and text for this catalogue will also be removed.`)) return;
    try {
      await adminApi().delete(`/admin/catalogues/${c.slug}/permanent`);
      toast.success(`"${c.name}" permanently deleted`);
      reloadDeletedCatalogues();
    } catch (err) { toast.error(err?.response?.data?.detail || "Permanent delete failed"); }
  };
  const restoreOrder = async (o) => {
    try {
      await adminApi().post(`/admin/orders/${o.id}/restore`);
      toast.success(`Order #${o.id.slice(0, 8)} restored`);
      reloadDeletedOrders();
    } catch (err) { toast.error(err?.response?.data?.detail || "Restore failed"); }
  };
  const permanentlyDeleteOrder = async (o) => {
    if (!window.confirm(`Permanently delete order #${o.id.slice(0, 8)}?\n\nThis cannot be undone.`)) return;
    try {
      await adminApi().delete(`/admin/orders/${o.id}/permanent`);
      toast.success(`Order permanently deleted`);
      reloadDeletedOrders();
    } catch (err) { toast.error(err?.response?.data?.detail || "Permanent delete failed"); }
  };
  const restoreReview = async (r) => {
    try {
      await adminApi().post(`/admin/reviews/${r.id}/restore`);
      toast.success("Review restored");
      reloadDeletedReviews();
    } catch (err) { toast.error(err?.response?.data?.detail || "Restore failed"); }
  };
  const permanentlyDeleteReview = async (r) => {
    if (!window.confirm(`Permanently delete review by ${r.name || "Anonymous"}?\n\nThis cannot be undone.`)) return;
    try {
      await adminApi().delete(`/admin/reviews/${r.id}/permanent`);
      toast.success("Review permanently deleted");
      reloadDeletedReviews();
    } catch (err) { toast.error(err?.response?.data?.detail || "Permanent delete failed"); }
  };
  const restoreTile = async (t) => {
    try {
      await adminApi().post(`/admin/custom-tiles/${t.id}/restore`);
      toast.success(`Restored "${t.title}"`);
      reloadDeletedTiles();
    } catch (err) { toast.error(err?.response?.data?.detail || "Restore failed"); }
  };
  const permanentlyDeleteTile = async (t) => {
    if (!window.confirm(`Permanently delete tile "${t.title}"?\n\nThis cannot be undone.`)) return;
    try {
      await adminApi().delete(`/admin/custom-tiles/${t.id}/permanent`);
      toast.success(`Tile permanently deleted`);
      reloadDeletedTiles();
    } catch (err) { toast.error(err?.response?.data?.detail || "Permanent delete failed"); }
  };

  const daysInfo = (iso) => {
    const daysAgo = iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000))) : 0;
    const daysLeft = Math.max(0, 30 - daysAgo);
    return { daysAgo, daysLeft, urgent: daysLeft <= 5 };
  };

  const TrashRow = ({ testId, cover, title, meta, onRestore, onDelete }) => (
    <div className="bg-white border border-[#E5DBD8] rounded-lg p-3 flex gap-3" data-testid={testId}>
      {cover ? (
        <img src={cover} alt="" className="w-16 h-16 object-cover rounded shrink-0 opacity-80"/>
      ) : (
        <div className="w-16 h-16 bg-[#F2E8E4] rounded shrink-0 flex items-center justify-center text-[10px] text-[#5C5555] font-doodle">—</div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-serif text-sm text-[#2D2424] leading-tight truncate">{title}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">{meta}</div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
        <button type="button" onClick={onRestore} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider bg-[#7A1F2B] text-white rounded hover:bg-[#5C1520] whitespace-nowrap">
          <RotateCcw className="w-3.5 h-3.5"/> Restore
        </button>
        <button type="button" onClick={onDelete} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider bg-white text-[#7A1F2B] border border-[#E5DBD8] rounded hover:bg-[#FFE7F1] whitespace-nowrap">
          <Trash2 className="w-3.5 h-3.5"/> Delete forever
        </button>
      </div>
    </div>
  );

  return (
    <div className="mt-6" data-testid="admin-recently-deleted-tab">
      <div className="mb-4 px-3 py-2.5 bg-[#FFF6F0] border border-dashed border-[#7A1F2B]/40 rounded flex items-start gap-2">
        <RotateCcw className="w-4 h-4 text-[#7A1F2B] mt-0.5 shrink-0"/>
        <p className="text-xs text-[#5C5555] leading-relaxed">
          Items moved here can be <b className="text-[#7A1F2B]">restored within 30 days</b>. After that they are permanently removed automatically. Deleted items don&apos;t appear on the storefront or in normal admin lists.
        </p>
      </div>

      {/* Deleted Products */}
      <h3 className="font-serif text-xl text-[#7A1F2B] uppercase tracking-wider mb-3 flex items-center gap-2"><Package className="w-4 h-4"/> Products <span className="text-xs text-[#5C5555] normal-case font-sans">({deletedProducts.length})</span></h3>
      {deletedProducts.length === 0 ? (
        <p className="text-center text-[#5C5555] py-6 italic font-hand">No deleted products.</p>
      ) : (
        <div className="flex flex-col gap-2.5 mb-8">
          {deletedProducts.map((p) => {
            const { daysAgo, daysLeft, urgent } = daysInfo(p.deleted_at);
            return (
              <TrashRow
                key={p.id}
                testId={`deleted-row-${p.slug}`}
                cover={p.images?.[0]}
                title={p.name}
                meta={<>
                  <span className="text-[#5C5555]">Deleted {daysAgo === 0 ? "today" : `${daysAgo}d ago`}</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-doodle ${urgent ? "bg-[#7A1F2B] text-white" : "bg-[#F2E8E4] text-[#2D2424]"}`}>{daysLeft} day{daysLeft !== 1 ? "s" : ""} left</span>
                  <span className="text-[#2D2424]">₹{(p.price || 0).toLocaleString("en-IN")}</span>
                </>}
                onRestore={() => restoreProduct(p)}
                onDelete={() => permanentlyDeleteProduct(p)}
              />
            );
          })}
        </div>
      )}

      {/* Deleted Catalogues */}
      <h3 className="font-serif text-xl text-[#7A1F2B] uppercase tracking-wider mb-3 flex items-center gap-2"><Grid2X2 className="w-4 h-4"/> Catalogues, Combos &amp; Trousseau <span className="text-xs text-[#5C5555] normal-case font-sans">({deletedCatalogues.length})</span></h3>
      {deletedCatalogues.length === 0 ? (
        <p className="text-center text-[#5C5555] py-6 italic font-hand">No deleted catalogues.</p>
      ) : (
        <div className="flex flex-col gap-2.5 mb-8">
          {deletedCatalogues.map((c) => {
            const { daysAgo, daysLeft, urgent } = daysInfo(c.deleted_at);
            const kindLabel = c.kind === "combo" ? "Combo" : c.kind === "trousseau" ? "Trousseau" : "Catalogue";
            return (
              <TrashRow
                key={c.slug}
                testId={`deleted-cat-row-${c.slug}`}
                cover={c.images?.[0]}
                title={c.name}
                meta={<>
                  <span className="px-1.5 py-0.5 rounded-full bg-[#FFE7F1] text-[#7A1F2B] font-doodle">{kindLabel}</span>
                  <span className="text-[#5C5555]">Deleted {daysAgo === 0 ? "today" : `${daysAgo}d ago`}</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-doodle ${urgent ? "bg-[#7A1F2B] text-white" : "bg-[#F2E8E4] text-[#2D2424]"}`}>{daysLeft} day{daysLeft !== 1 ? "s" : ""} left</span>
                  <span className="text-[#2D2424]">/{c.slug}</span>
                </>}
                onRestore={() => restoreCatalogue(c)}
                onDelete={() => permanentlyDeleteCatalogue(c)}
              />
            );
          })}
        </div>
      )}

      {/* Deleted Orders */}
      <h3 className="font-serif text-xl text-[#7A1F2B] uppercase tracking-wider mb-3 flex items-center gap-2"><Package className="w-4 h-4"/> Orders <span className="text-xs text-[#5C5555] normal-case font-sans">({deletedOrders.length})</span></h3>
      {deletedOrders.length === 0 ? (
        <p className="text-center text-[#5C5555] py-6 italic font-hand">No deleted orders.</p>
      ) : (
        <div className="flex flex-col gap-2.5 mb-8">
          {deletedOrders.map((o) => {
            const { daysAgo, daysLeft, urgent } = daysInfo(o.deleted_at);
            return (
              <TrashRow
                key={o.id}
                testId={`deleted-order-row-${o.id}`}
                cover={null}
                title={`Order #${o.id.slice(0, 8).toUpperCase()} · ${o.customer?.name || "—"}`}
                meta={<>
                  <span className="text-[#5C5555]">Deleted {daysAgo === 0 ? "today" : `${daysAgo}d ago`}</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-doodle ${urgent ? "bg-[#7A1F2B] text-white" : "bg-[#F2E8E4] text-[#2D2424]"}`}>{daysLeft} day{daysLeft !== 1 ? "s" : ""} left</span>
                  <span className="text-[#2D2424]">₹{(o.total || 0).toLocaleString("en-IN")}</span>
                  <span className={`uppercase tracking-wider ${o.status === "paid" ? "text-green-700" : "text-amber-700"}`}>{o.status}</span>
                </>}
                onRestore={() => restoreOrder(o)}
                onDelete={() => permanentlyDeleteOrder(o)}
              />
            );
          })}
        </div>
      )}

      {/* Deleted Reviews */}
      <h3 className="font-serif text-xl text-[#7A1F2B] uppercase tracking-wider mb-3 flex items-center gap-2"><Star className="w-4 h-4"/> Reviews <span className="text-xs text-[#5C5555] normal-case font-sans">({deletedReviews.length})</span></h3>
      {deletedReviews.length === 0 ? (
        <p className="text-center text-[#5C5555] py-6 italic font-hand">No deleted reviews.</p>
      ) : (
        <div className="flex flex-col gap-2.5 mb-8">
          {deletedReviews.map((r) => {
            const { daysAgo, daysLeft, urgent } = daysInfo(r.deleted_at);
            return (
              <TrashRow
                key={r.id}
                testId={`deleted-review-row-${r.id}`}
                cover={null}
                title={`${r.name || "Anonymous"} · ${r.rating || 0}★`}
                meta={<>
                  <span className="text-[#5C5555]">Deleted {daysAgo === 0 ? "today" : `${daysAgo}d ago`}</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-doodle ${urgent ? "bg-[#7A1F2B] text-white" : "bg-[#F2E8E4] text-[#2D2424]"}`}>{daysLeft} day{daysLeft !== 1 ? "s" : ""} left</span>
                  <span className="text-[#2D2424] italic truncate max-w-[280px]">&ldquo;{(r.comment || "").slice(0, 60)}&rdquo;</span>
                </>}
                onRestore={() => restoreReview(r)}
                onDelete={() => permanentlyDeleteReview(r)}
              />
            );
          })}
        </div>
      )}

      {/* Deleted Custom Tiles */}
      <h3 className="font-serif text-xl text-[#7A1F2B] uppercase tracking-wider mb-3 flex items-center gap-2"><Grid2X2 className="w-4 h-4"/> Custom Tiles <span className="text-xs text-[#5C5555] normal-case font-sans">({deletedTiles.length})</span></h3>
      {deletedTiles.length === 0 ? (
        <p className="text-center text-[#5C5555] py-6 italic font-hand">No deleted custom tiles.</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {deletedTiles.map((t) => {
            const { daysAgo, daysLeft, urgent } = daysInfo(t.deleted_at);
            return (
              <TrashRow
                key={t.id}
                testId={`deleted-tile-row-${t.id}`}
                cover={t.image_url}
                title={t.title || "(untitled)"}
                meta={<>
                  <span className="px-1.5 py-0.5 rounded-full bg-[#FFE7F1] text-[#7A1F2B] font-doodle">{t.section || "home"}</span>
                  <span className="text-[#5C5555]">Deleted {daysAgo === 0 ? "today" : `${daysAgo}d ago`}</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-doodle ${urgent ? "bg-[#7A1F2B] text-white" : "bg-[#F2E8E4] text-[#2D2424]"}`}>{daysLeft} day{daysLeft !== 1 ? "s" : ""} left</span>
                </>}
                onRestore={() => restoreTile(t)}
                onDelete={() => permanentlyDeleteTile(t)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
